/** Helpers for AMP (Amazon Managed Prometheus) alert create/update payloads and list normalization. */
import { GRAFANA_URL, GRAFANA_PROM_DS_UID } from "../services/config";

export const AMP_PLATFORM = "amp";
export const GRAFANA_PLATFORM = "grafana";

/**
 * Build a Grafana Explore deep link for AMP/Prometheus metrics.
 * Uses datasource UID (never the type name "prometheus") so Explore opens the AMP DS.
 */
export function buildAmpExploreMetricsUrl(expression, { rangeSec = 300, datasourceUid } = {}) {
  const expr = String(expression || "").trim();
  if (!expr) return "";
  const uid = String(datasourceUid || GRAFANA_PROM_DS_UID || "").trim() || "prometheus-clairai";
  const from = `now-${Math.max(60, Number(rangeSec) || 300)}s`;
  const panes = {
    metrics: {
      datasource: uid,
      queries: [{ refId: "A", expr, range: true, instant: false }],
      range: { from, to: "now" },
    },
  };
  return (
    `${GRAFANA_URL.replace(/\/$/, "")}/explore` +
    `?orgId=1&schemaVersion=1&panes=${encodeURIComponent(JSON.stringify(panes))}`
  );
}

/** True when Explore URL used type name "prometheus" instead of a real UID (opens Loki incorrectly). */
export function isBrokenAmpExploreDatasourceUrl(url) {
  const raw = String(url || "");
  if (!raw) return false;
  return (
    raw.includes('"datasource":"prometheus"') ||
    raw.includes('%22datasource%22%3A%22prometheus%22') ||
    raw.includes("%22datasource%22%3A%22prometheus%22")
  );
}

/** Rewrite broken AMP View Data URLs to use the Prometheus/AMP datasource UID. */
export function ensureAmpExploreMetricsUrl(url, expression, rangeSec = 300, datasourceUid) {
  if (!url && !expression) return "";
  if (url && !isBrokenAmpExploreDatasourceUrl(url) && url.includes("/explore")) {
    return url;
  }
  return buildAmpExploreMetricsUrl(expression, { rangeSec, datasourceUid }) || url || "";
}

export function secondsToForDuration(seconds) {
  if (seconds == null || seconds === undefined) return "5m";
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return "5m";
  if (s >= 3600) return `${Math.floor(s / 3600)}h`;
  if (s >= 60) return `${Math.floor(s / 60)}m`;
  return `${s}s`;
}

export function parseAmpDurationToSeconds(duration) {
  const raw = String(duration || "").trim().toLowerCase();
  const match = raw.match(/^(\d+)([smhdw])$/);
  if (!match) return 300;
  const value = Number(match[1]);
  const unit = match[2];
  if (unit === "h") return value * 3600;
  if (unit === "d") return value * 86400;
  if (unit === "m") return value * 60;
  if (unit === "w") return value * 604800;
  return value;
}

const OPERATOR_MAP = {
  ">": ">",
  ">=": ">=",
  "<": "<",
  "<=": "<=",
  "==": "==",
};

/** Split a deployed AMP boolean expression into builder fields when possible. */
export function parseAmpExpressionParts(expression) {
  const expr = String(expression || "").trim();
  const match = expr.match(/^(.+?)\s*(>=|<=|==|!=|>|<)\s*(-?[\d.]+)\s*$/);
  if (!match) {
    return { metricExpr: expr, operator: ">", threshold: 0 };
  }
  return {
    metricExpr: match[1].trim(),
    operator: match[2],
    threshold: parseFloat(match[3]),
  };
}

/** Combine metric query + threshold into a single boolean PromQL expression for AMP. */
export function buildAmpExpression(metricExpr, operator, threshold) {
  const expr = String(metricExpr || "").trim();
  if (!expr) return "";

  if (/\s(?:>|<|>=|<=|==|!=)\s*[\d.]+(?:\s*)$/.test(expr)) {
    return expr;
  }

  const op = OPERATOR_MAP[operator] || ">";
  const thresholdNum = Number(threshold);
  const resolvedThreshold = Number.isFinite(thresholdNum) ? thresholdNum : 0;
  return `${expr} ${op} ${resolvedThreshold}`;
}

export function resolveNotificationReceiver(notificationChannels) {
  if (Array.isArray(notificationChannels)) {
    return notificationChannels[0] || "";
  }
  return notificationChannels || "";
}

export function buildAmpAlertPayload(vals, promExpr, accountId) {
  const receiver = resolveNotificationReceiver(vals.notificationChannels);
  const description = vals.description?.trim() || "";
  const summary = description || vals.title;
  const extraLabels = { ...(vals.extraLabels || {}) };
  if (vals.enableAutoRca) {
    extraLabels.auto_rca = "true";
  } else {
    delete extraLabels.auto_rca;
  }
  const labels = {
    severity: vals.severity || "critical",
    ...extraLabels,
  };

  const expression = buildAmpExpression(promExpr, vals.operator || ">", vals.threshold);
  const annotations = {
    summary,
    ...(description ? { description } : {}),
  };

  if (vals.includeDashboard && vals.dashboard_uid) {
    annotations.Dashboard = `${GRAFANA_URL.replace(/\/$/, "")}/d/${vals.dashboard_uid}?orgId=1`;
  }
  if (vals.viewData && expression) {
    const rangeSec = Number(vals.relativeFrom || 300);
    // Use Prometheus/AMP datasource UID — type name "prometheus" makes Grafana Explore fall back to Loki.
    annotations.ExploreMetricsURL = buildAmpExploreMetricsUrl(expression, {
      rangeSec,
      datasourceUid: vals.prometheusDsUid || GRAFANA_PROM_DS_UID,
    });
  }

  return {
    alertName: vals.title,
    group: vals.ruleGroup || "app-alerts",
    expression,
    duration: secondsToForDuration(vals.relativeFrom),
    severity: vals.severity || "critical",
    receiver,
    labels,
    annotations,
    ...(accountId ? { account_id: accountId } : {}),
  };
}

export function resolveAmpRuntimeStatus(alert) {
  const raw = String(
    alert?.ruleEvaluationState ||
      alert?.evaluationState ||
      alert?.state ||
      "normal",
  ).toLowerCase();
  if (raw === "active") return "firing";
  if (raw === "inactive") return "normal";
  return raw;
}

/**
 * Resolve Explore / View Data URL from AMP annotations.
 * Save path writes Prom-safe keys (ExploreMetricsURL / ExploreLogsURL);
 * older alerts may still have "View Data*" keys.
 */
export function resolveAmpViewDataUrl(annotations = {}, expression = "", rangeSec = 300) {
  const ann = annotations || {};
  const raw =
    ann.ExploreMetricsURL ||
    ann.ExploreLogsURL ||
    ann["View Data"] ||
    ann["View Data (Prometheus)"] ||
    ann["View Data (Loki)"] ||
    "";
  return ensureAmpExploreMetricsUrl(raw, expression, rangeSec);
}

/** Normalize AMP API list item into the shared alert row shape used by the rules UI. */
export function normalizeAmpAlert(item) {
  if (!item || item.status === "DELETED") return null;

  const receiver = item.receiver || item.labels?.clairai_receiver || "";
  const channels = receiver ? [receiver] : [];
  const { metricExpr, operator, threshold } = parseAmpExpressionParts(item.expression);
  const deployStatus = String(item.status || "UNKNOWN").toUpperCase();
  const errorDetails = item.error_details;
  const deployError =
    (typeof errorDetails === "string" && errorDetails) ||
    errorDetails?.message ||
    item.error_message ||
    null;

  return {
    platform: AMP_PLATFORM,
    id: item.alert_id,
    title: item.alert_name,
    ruleGroup: item.group_name,
    expression: item.expression || "",
    metricExpression: metricExpr,
    operator,
    threshold_value: threshold,
    duration: item.duration,
    relativeFrom: parseAmpDurationToSeconds(item.duration),
    relativeTo: 0,
    severity: String(item.labels?.severity || item.severity || "warning").toLowerCase(),
    notificationChannels: channels,
    labels: {
      alertname: item.alert_name,
      ...(item.labels || {}),
    },
    annotations: item.annotations || {},
    enabled: deployStatus === "ACTIVE",
    ampDeployStatus: deployStatus,
    ampDeployError: deployError,
    ruleEvaluationState: item.rule_evaluation_state || null,
    data: [],
    dataSource: "prometheus",
    evaluationState:
      item.rule_evaluation_state ||
      (deployStatus === "ACTIVE" ? null : deployStatus.toLowerCase()),
    state: item.rule_evaluation_state || null,
    isSilenced: false,
    silenceId: undefined,
    viewData: resolveAmpViewDataUrl(
      item.annotations,
      item.expression || "",
      parseAmpDurationToSeconds(item.duration),
    ),
    viewDashboard: item.annotations?.Dashboard || "",
    lastTriggered: "—",
    triggerCount: 0,
  };
}

export function isAmpAlert(alert) {
  return alert?.platform === AMP_PLATFORM;
}

export function getAlertPlatformLabel(platform) {
  return platform === AMP_PLATFORM ? "AMP" : "Grafana";
}

const GRAFANA_EVALUATOR_TO_OP = {
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  eq: "==",
};

function grafanaDataHasThreshold(item) {
  const params = item?.model?.conditions?.[0]?.evaluator?.params;
  return Array.isArray(params) && params.length > 0;
}

export function getGrafanaQueryExpr(alertData = []) {
  const prom = alertData.find(
    (item) => item?.model?.datasource?.type === "prometheus" && item?.model?.expr,
  );
  if (prom?.model?.expr) return String(prom.model.expr).trim();

  const loki = alertData.find(
    (item) => item?.model?.datasource?.type === "loki" && item?.model?.expr,
  );
  if (loki?.model?.expr) return String(loki.model.expr).trim();

  const any = alertData.find((item) => item?.model?.expr);
  return String(any?.model?.expr || "").trim();
}

export function getGrafanaThresholdRule(alertData = [], conditionRefId) {
  return (
    alertData.find((item) => item?.refId === conditionRefId && grafanaDataHasThreshold(item)) ||
    alertData.find(
      (item) => item?.model?.refId === conditionRefId && grafanaDataHasThreshold(item),
    ) ||
    alertData.find(
      (item) => item?.model?.type === "threshold" && grafanaDataHasThreshold(item),
    ) ||
    alertData.find(grafanaDataHasThreshold) ||
    null
  );
}

export function getGrafanaThresholdInfo(alert) {
  const alertData = alert?.data || [];
  const conditionRefId = alert?.condition;
  const thresholdRule = getGrafanaThresholdRule(alertData, conditionRefId);
  const evaluator = thresholdRule?.model?.conditions?.[0]?.evaluator;
  const params = evaluator?.params;

  let threshold = alert?.threshold_value;
  if (
    (threshold === undefined || threshold === null || threshold === "") &&
    Array.isArray(params) &&
    params.length > 0
  ) {
    threshold = params[0];
  }

  let operator = alert?.operator;
  if (!operator && evaluator?.type) {
    operator = GRAFANA_EVALUATOR_TO_OP[evaluator.type] || ">";
  }

  return {
    threshold,
    operator: operator || ">",
  };
}

/** Full boolean expression for display (metric + threshold), matching AMP cards. */
export function getAlertDisplayExpression(alert) {
  if (isAmpAlert(alert)) {
    return alert.expression || alert.metricExpression || "";
  }

  const metricExpr = getGrafanaQueryExpr(alert?.data || []);
  if (!metricExpr) return "";

  const { threshold, operator } = getGrafanaThresholdInfo(alert);
  if (threshold === undefined || threshold === null || threshold === "") {
    return metricExpr;
  }

  return buildAmpExpression(metricExpr, operator, threshold);
}

export function getAlertDisplayDuration(alert) {
  if (isAmpAlert(alert)) {
    return alert.duration || (alert.relativeFrom ? secondsToForDuration(alert.relativeFrom) : "");
  }

  if (alert.for_duration) return alert.for_duration;
  if (alert.duration) return alert.duration;
  if (alert.for) return alert.for;
  return "";
}

export function getAlertDisplayReceiver(alert) {
  return resolveNotificationReceiver(alert?.notificationChannels);
}
